import type { Narrator } from '../types';

/**
 * Spoken narration in Bahasa Indonesia.
 *
 * The RPPH's note to the app team asks for automatic narration on every screen,
 * because four- and five-year-olds cannot read the instructions. This is the
 * cheapest thing that satisfies that today.
 *
 * It is deliberately a thin interface over `speechSynthesis` so that swapping to
 * recorded voice-over later is a change to this file and nothing else. Recorded
 * audio is almost certainly what ships - a synthesised voice reading to a
 * classroom sounds like a synthesised voice reading to a classroom - but the
 * question of who records it is open, and the rest of the app should not wait
 * on the answer.
 *
 * Two device realities shape this:
 *
 *  - Voices load asynchronously, and on a cold page `getVoices()` returns an
 *    empty list until `voiceschanged` fires. Speaking before then silently gets
 *    the wrong voice, or none.
 *  - An Android WebView shipped without Google TTS data has no id-ID voice at
 *    all. That is a plausible IFP configuration and we cannot detect it from
 *    here in advance, so `available()` is reported honestly and the player
 *    always shows the narration text as a caption regardless. A teacher can
 *    read a caption aloud; a silent screen with no text is unrecoverable.
 */

const LANG = 'id-ID';

/**
 * Narration is switched off for now (26 Sep).
 *
 * The synthesised voice was standing in for recorded voice-over, and it was
 * doing the lessons more harm than good in front of people: a machine reading
 * to four-year-olds is the first thing anyone notices. Until the lines are
 * recorded, every screen stays silent and its caption carries the narration -
 * which the player already guarantees on every screen.
 *
 * What still needs recording, and the exact script for each line, is on
 * `#/suara`. Flip this back to `true` to hear the synthesised voice again; the
 * rest of the app does not need to change.
 *
 * Effects are not affected: the taps, chimes and nudges in `feedback.ts` are a
 * separate synth and keep playing.
 */
export const NARRATION_ENABLED = false;

export function createNarrator(): Narrator {
  const synth = window.speechSynthesis as SpeechSynthesis | undefined;
  let voice: SpeechSynthesisVoice | null = null;
  let disposed = false;

  const pickVoice = (): void => {
    if (!synth) return;
    const voices = synth.getVoices();
    voice =
      voices.find((v) => v.lang === LANG) ??
      // Some builds report "id_ID" or "id"; accept anything Indonesian before
      // giving up, since a regional variant is far better than an English voice
      // attempting Bahasa.
      voices.find((v) => v.lang.replace('_', '-').startsWith('id')) ??
      null;
  };

  pickVoice();
  synth?.addEventListener?.('voiceschanged', pickVoice);

  return {
    speak(text) {
      if (!NARRATION_ENABLED || !synth || disposed || !text.trim()) return;

      // Cancel first: utterances queue by default, so a child tapping through
      // three hotspots would hear all three read in sequence long after they
      // moved on. The most recent tap is the only one that is still true.
      synth.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = LANG;
      if (voice) utterance.voice = voice;
      // Slower than default. Early-years narration is paced for comprehension,
      // and the stock rate is tuned for adults skimming.
      utterance.rate = 0.9;
      utterance.pitch = 1.05;

      try {
        synth.speak(utterance);
      } catch {
        // Autoplay policy, or a WebView with the engine stubbed out. Captions
        // are on screen either way.
      }
    },

    stop() {
      try {
        synth?.cancel();
      } catch {
        // Nothing useful to do; the next screen cancels again anyway.
      }
    },

    available: () => NARRATION_ENABLED && Boolean(synth) && voice !== null,

    destroy() {
      disposed = true;
      synth?.removeEventListener?.('voiceschanged', pickVoice);
      try {
        synth?.cancel();
      } catch {
        // Ignored - we are tearing down.
      }
    },
  };
}
